import { c as _c } from "react/compiler-runtime";
import { DateFilterChip } from "@components/common/faro-table/faro-table-filter/date-filter/date-filter-chip";
import { NumberRangeFilterChip } from "@components/common/faro-table/faro-table-filter/number-range-filter/number-range-filter-chip";
import { GROUPS_CREATED_FILTER_COLUMN, GROUPS_FILTER_COLUMNS, GROUPS_MANAGERS_FILTER_COLUMN, GROUPS_PROJECTS_FILTER_COLUMN } from "@components/groups/groups-filters/groups-filter-constants";
import { getGroupsCreatedFilterPresets, getGroupsFilterOptions } from "@components/groups/groups-filters/groups-filter-options";
import { GROUPS_TABLE_COLUMN_LABELS } from "@components/table/groups/groups-table-constants";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { doesColumnPassAnyFilter } from "@hooks/table-filters/use-apply-filter-utils";
import { useFilterQueryParams } from "@hooks/table-filters/use-filter-query-params";
import { fetchingSdbCompanyFlagsSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { setTableType } from "@store/table/table-slice";
import { TableType } from "@utils/track-event/track-event-list";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/** The filter the user applied to a column, which the column offers */

/**
 * Applies the active projects, managers and creation date filters to the groups and returns the
 * chips to change them. The groups are all fetched at once, so they are filtered here.
 * Workspaces that cannot assign managers have no managers filter, like they have no members column.
 */
export function useGroupsFilters() {
  const $ = _c(17);
  const {
    getFilterIdsForColumn
  } = useFilterQueryParams();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const {
    fetchSelectedCompanyContext
  } = useAppSelector(fetchingSdbCompanyFlagsSelector);
  const columns = getGroupsFilterColumns(canAssignManagers);
  const shouldRemoveManagersFilter = !canAssignManagers && fetchSelectedCompanyContext === "succeeded";
  const dispatch = useAppDispatch();
  let t0;
  let t1;
  if ($[0] !== dispatch) {
    t0 = () => {
      dispatch(setTableType(TableType.groups));
    };
    t1 = [dispatch];
    $[0] = dispatch;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== getFilterIdsForColumn) {
    t2 = column => {
      const filterIds = getFilterIdsForColumn(column);
      return {
        column,
        filterIds,
        activeFilterId: getActiveFilterId(column, filterIds)
      };
    };
    $[3] = getFilterIdsForColumn;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const filtersByColumn = columns.map(t2);
  const isAnyFilterApplied = filtersByColumn.some(_temp);
  const hasIgnoredFilterIds = shouldRemoveManagersFilter && getFilterIdsForColumn(GROUPS_MANAGERS_FILTER_COLUMN).length > 0 || filtersByColumn.some(_temp2);
  const [, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!hasIgnoredFilterIds) {
      return;
    }
    setSearchParams(searchParams => {
      getGroupsFilterColumns(canAssignManagers).forEach(column_0 => {
        const activeFilterId_1 = getActiveFilterId(column_0, searchParams.getAll(column_0));
        searchParams.delete(column_0);
        if (activeFilterId_1 !== undefined) {
          searchParams.append(column_0, activeFilterId_1);
        }
      });
      if (shouldRemoveManagersFilter) {
        searchParams.delete(GROUPS_MANAGERS_FILTER_COLUMN);
      }
      return searchParams;
    }, {
      replace: true
    });
  }, [hasIgnoredFilterIds, canAssignManagers, shouldRemoveManagersFilter, setSearchParams]);
  const activeFiltersKey = JSON.stringify(filtersByColumn.flatMap(_temp3));
  let t3;
  if ($[5] !== activeFiltersKey) {
    t3 = groups => {
      const activeFilters = JSON.parse(activeFiltersKey);
      if (activeFilters.length === 0) {
        return groups;
      }
      const columnFilters = activeFilters.map(_temp4);
      return groups.filter(group => columnFilters.every(columnFilter => doesColumnPassAnyFilter({
        entity: group,
        ...columnFilter
      })));
    };
    $[5] = activeFiltersKey;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const filterGroups = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <NumberRangeFilterChip key={GROUPS_PROJECTS_FILTER_COLUMN} label={GROUPS_TABLE_COLUMN_LABELS.projects} filteredColumn={GROUPS_PROJECTS_FILTER_COLUMN} tableType={TableType.groups} />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== canAssignManagers) {
    t5 = canAssignManagers ? <NumberRangeFilterChip key={GROUPS_MANAGERS_FILTER_COLUMN} label="Managers" filteredColumn={GROUPS_MANAGERS_FILTER_COLUMN} tableType={TableType.groups} /> : null;
    $[8] = canAssignManagers;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DateFilterChip key={GROUPS_CREATED_FILTER_COLUMN} label={GROUPS_TABLE_COLUMN_LABELS.createdDate} filteredColumn={GROUPS_CREATED_FILTER_COLUMN} presets={getGroupsCreatedFilterPresets()} tableType={TableType.groups} />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5) {
    t7 = {
      [GROUPS_PROJECTS_FILTER_COLUMN]: t4,
      [GROUPS_MANAGERS_FILTER_COLUMN]: t5,
      [GROUPS_CREATED_FILTER_COLUMN]: t6
    };
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const filterChips = t7;
  let t8;
  if ($[13] !== filterChips || $[14] !== filterGroups || $[15] !== isAnyFilterApplied) {
    t8 = {
      isAnyFilterApplied,
      filterGroups,
      filterChips
    };
    $[13] = filterChips;
    $[14] = filterGroups;
    $[15] = isAnyFilterApplied;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}

/** Returns the columns the groups can be filtered by, which include the managers only if the workspace can assign them */
function _temp4(t0) {
  const [column_2, activeFilterId_3] = t0;
  return {
    activeFilterIds: [activeFilterId_3],
    allPossibleFiltersForColumn: getGroupsFilterOptions(column_2, [activeFilterId_3])
  };
}
function _temp3(t0) {
  const {
    column: column_1,
    activeFilterId: activeFilterId_2
  } = t0;
  return activeFilterId_2 === undefined ? [] : [[column_1, activeFilterId_2]];
}
function _temp2(t0) {
  const {
    filterIds: filterIds_0,
    activeFilterId: activeFilterId_0
  } = t0;
  return filterIds_0.length > (activeFilterId_0 === undefined ? 0 : 1);
}
function _temp(t0) {
  const {
    activeFilterId
  } = t0;
  return activeFilterId !== undefined;
}
function getGroupsFilterColumns(canAssignManagers) {
  return GROUPS_FILTER_COLUMNS.filter(column => canAssignManagers || column !== GROUPS_MANAGERS_FILTER_COLUMN);
}

/** Returns the first of the IDs that is a filter the column offers, which is the one it applies */
function getActiveFilterId(column, filterIds) {
  const filters = getGroupsFilterOptions(column, filterIds);
  return filterIds.find(filterId => filterId in filters);
}